import logger from '@/logger/index'
import { parse, addMonths, isAfter } from 'date-fns'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

const STORAGE_KEY = 'userTips'

// Variável para rastrear se o scroll está bloqueado pelo tooltip
let scrollLocked = false
let scrollLockTimeout = null

function checkTipValidity (startDate, validForMonths) {
  if (!startDate || !validForMonths) {
    return true // Se não tem validação de data, sempre válido
  }

  try {
    const start = parse(startDate, 'dd/MM/yyyy', new Date())

    // Verifica se a data é válida
    if (isNaN(start.getTime())) {
      logger.debug('Data de início do tooltip inválida:', startDate)
      return true
    }

    // Calcula a data de expiração (startDate + validForMonths)
    const expirationDate = addMonths(start, validForMonths)

    const now = new Date()

    // Verifica se ainda está dentro do período válido
    // Retorna true se a data atual é anterior ou igual à data de expiração
    return !isAfter(now, expirationDate)
  } catch (error) {
    logger.debug('Erro ao verificar validade do tooltip:', error)
    return true
  }
}

const state = {
  seenTips: [],
  currentTip: null,
}

const getters = {
  hasSeenTip: (state) => (tipKey) => {
    return state.seenTips.includes(tipKey)
  },
  getCurrentTip: (state) => state.currentTip,
  isShowingTip: (state) => state.currentTip !== null,
}

const mutations = {
  SET_SEEN_TIPS (state, tips) {
    state.seenTips = tips
  },
  ADD_SEEN_TIP (state, tipKey) {
    if (!state.seenTips.includes(tipKey)) {
      state.seenTips.push(tipKey)
    }
  },
  SET_CURRENT_TIP (state, tip) {
    state.currentTip = tip
  },
  CLEAR_CURRENT_TIP (state) {
    state.currentTip = null
  },
}

const actions = {
  loadSeenTips ({ commit }) {
    try {
      const storedTips = localStorage.getItem(STORAGE_KEY)
      if (storedTips) {
        const tips = JSON.parse(storedTips)
        commit('SET_SEEN_TIPS', tips)
      }
    } catch (error) {
      logger.debug('Erro ao carregar dicas vistas:', error)
      commit('SET_SEEN_TIPS', [])
    }
  },

  markTipAsSeen ({ commit, state }, tipKey) {
    commit('ADD_SEEN_TIP', tipKey)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.seenTips))
    } catch (error) {
      logger.debug('Erro ao salvar dica vista:', error)
    }
  },

  showTip ({ commit, getters }, tipConfig) {
    // Não mostra se já foi vista
    if (getters.hasSeenTip(tipConfig.key)) {
      return false
    }

    // Verifica se está dentro do período válido (se startDate e validForMonths foram fornecidos)
    if (tipConfig.startDate && tipConfig.validForMonths) {
      const isValid = checkTipValidity(tipConfig.startDate, tipConfig.validForMonths)
      if (!isValid) {
        logger.debug(`Tooltip "${tipConfig.key}" expirado. Data de início: ${tipConfig.startDate}, Válido por: ${tipConfig.validForMonths} meses`)
        return false
      }
    }

    // Bloqueia o scroll ANTES de exibir o tooltip
    if (!scrollLocked) {
      disableBodyScroll(document.body, {
        reserveScrollBarGap: true,
      })
      scrollLocked = true

      // Timeout para liberar o scroll se o tooltip não aparecer em tempo razoável
      if (scrollLockTimeout) {
        clearTimeout(scrollLockTimeout)
      }
      const delay = tipConfig.delay || 1000
      const maxWaitTime = Math.max(delay + 3000, 5000) // delay + 3s ou mínimo 5s

      scrollLockTimeout = setTimeout(() => {
        const currentState = getters.isShowingTip
        if (scrollLocked && !currentState) {
          enableBodyScroll(document.body)
          scrollLocked = false
          scrollLockTimeout = null
        }
      }, maxWaitTime)
    }

    commit('SET_CURRENT_TIP', {
      key: tipConfig.key,
      title: tipConfig.title || '',
      content: tipConfig.content,
      targetRef: tipConfig.targetRef,
      position: tipConfig.position || 'right',
      delay: tipConfig.delay || 1000,
    })

    return true
  },

  hideTip ({ commit, dispatch }, { tipKey, markAsSeen = true }) {
    if (markAsSeen) {
      dispatch('markTipAsSeen', tipKey)
    }
    commit('CLEAR_CURRENT_TIP')

    // Libera o scroll quando o tooltip é fechado (usuário clicou em "Entendi" ou pressionou Esc)
    if (scrollLocked) {
      if (scrollLockTimeout) {
        clearTimeout(scrollLockTimeout)
        scrollLockTimeout = null
      }
      enableBodyScroll(document.body)
      scrollLocked = false
    }
  },

  resetAllTips ({ commit }) {
    // Útil para desenvolvimento/testes
    commit('SET_SEEN_TIPS', [])
    commit('CLEAR_CURRENT_TIP')
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      logger.debug('Erro ao resetar dicas:', error)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
